import { getAuthHeader } from './authApi';

// Projects API utility
const isProduction = process.env.NODE_ENV === 'production';
const API_BASE_URL = isProduction ? '/api/v1' : (process.env.REACT_APP_API_URL || '/api/v1');

export interface Project {
  id: number;
  title: string;
  description: string;
  projectStatus: 'LIVE' | 'IN_DEVELOPMENT' | 'PLANNING' | 'COMPLETED' | 'ON_HOLD';
  projectUrl: string;
  technologies: string[];
  displayOrder: number;
  isActive: boolean;
  isFeatured: boolean;
}

export const projectsApi = {
  // Get all active projects
  async getAllProjects(): Promise<Project[]> {
    try {
      const response = await fetch(`${API_BASE_URL}/projects`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching projects:', error);
      // Return fallback data if API fails
      return [
        {
          id: 1,
          title: "K9HKR Studios Platform",
          description: "Our company homepage and portfolio platform built with React, TypeScript, and Spring Boot. Features modern responsive design, API integration, and deployment-ready architecture.",
          projectStatus: 'LIVE',
          projectUrl: "#",
          technologies: ["React", "TypeScript", "Spring Boot", "MySQL", "Docker"],
          displayOrder: 1,
          isActive: true,
          isFeatured: true
        },
        {
          id: 2,
          title: "Enterprise Dashboard",
          description: "Advanced analytics dashboard with real-time data visualization, user management, and customizable reporting features for enterprise clients.",
          projectStatus: 'IN_DEVELOPMENT',
          projectUrl: "#",
          technologies: ["React", "Chart.js", "Node.js", "MongoDB"],
          displayOrder: 2,
          isActive: true,
          isFeatured: true
        },
        {
          id: 3,
          title: "E-Commerce Platform",
          description: "Modern e-commerce solution with inventory management, payment processing, order tracking, and administrative tools for online businesses.",
          projectStatus: 'PLANNING',
          projectUrl: "#",
          technologies: ["Next.js", "Stripe", "PostgreSQL", "Redis"],
          displayOrder: 3,
          isActive: true,
          isFeatured: true
        }
      ];
    }
  },

  // Get featured projects only
  async getFeaturedProjects(): Promise<Project[]> {
    try {
      const response = await fetch(`${API_BASE_URL}/projects/featured`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching featured projects:', error);
      // Return fallback to all projects
      return this.getAllProjects();
    }
  },

  // Get all projects including inactive (Admin)
  async getAllProjectsAdmin(): Promise<Project[]> {
    const response = await fetch(`${API_BASE_URL}/projects/all`, {
      headers: getAuthHeader()
    });
    if (!response.ok) throw new Error('Failed to fetch all projects');
    return response.json();
  },

  async addProject(project: Omit<Project, 'id'>): Promise<Project> {
    const response = await fetch(`${API_BASE_URL}/projects`, {
      method: 'POST',
      headers: {
        ...getAuthHeader(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(project),
    });
    if (!response.ok) throw new Error('Failed to add project');
    return response.json();
  },

  async updateProject(id: number, project: Partial<Project>): Promise<Project> {
    const response = await fetch(`${API_BASE_URL}/projects/${id}`, {
      method: 'PUT',
      headers: {
        ...getAuthHeader(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(project),
    });
    if (!response.ok) throw new Error('Failed to update project');
    return response.json();
  },

  async deleteProject(id: number): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/projects/${id}`, {
      method: 'DELETE',
      headers: getAuthHeader(),
    });
    if (!response.ok) throw new Error('Failed to delete project');
  }
};